import React from "react";
import { KEY_POSITIONS } from "../mockData";

type PhasePoint = { id: string; label: string; pct: number };

type Props = {
  active: string;
  onSelect: (id: string) => void;
  positions?: PhasePoint[];
  subtitle?: string;
  /** The session's activity id — the copy is the sport's ("Gait cycle phases", "% of cycle"),
   * not golf's for everyone (user review 2026-09-12). Absent = the mock golf page. */
  activity?: string;
};

/** The timeline's words per sport family. Golf is the default because the only caller without an
 * activity is the golf-only mock page. */
export function phaseCopyFor(activity?: string): { eyebrow: string; unit: string } {
  const a = activity ?? "golf";
  if (a.startsWith("golf")) return { eyebrow: "Golf swing phases", unit: "swing" };
  if (a === "runGait") return { eyebrow: "Gait cycle phases", unit: "cycle" };
  if (a === "verticalJump") return { eyebrow: "Jump phases", unit: "jump" };
  if (a.startsWith("squat")) return { eyebrow: "Squat phases", unit: "lift" }; // side, front and behind alike
  if (a.startsWith("tennis")) return { eyebrow: "Stroke phases", unit: "stroke" };
  if (a.startsWith("cricketBowling") || a === "bowling") return { eyebrow: "Delivery phases", unit: "delivery" };
  if (a === "cricketBatting") return { eyebrow: "Shot phases", unit: "shot" };
  return { eyebrow: "Movement phases", unit: "movement" };
}

export function PhaseTimeline({ active, onSelect, positions, subtitle, activity }: Props) {
  const points = positions ?? KEY_POSITIONS;
  const current = points.find((p) => p.id === active);
  const copy = phaseCopyFor(activity);
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow">{copy.eyebrow}</div>
          <div className="text-sm text-slate-600">
            {subtitle ?? "P1 → P10"}
          </div>
        </div>
      </div>

      <div className="relative pt-3 pb-1">
        {/* track */}
        <div className="h-1.5 rounded-full bg-slate-100 relative">
          <div
            className="absolute top-0 left-0 h-1.5 rounded-full bg-gradient-to-r from-el-300 to-el-500"
            style={{
              width: `${current?.pct ?? 0}%`,
            }}
          />
        </div>

        {/* markers */}
        <div className="absolute inset-x-0 top-2.5">
          {points.map((p) => {
            const isActive = p.id === active;
            // Anchor the label so wider text (e.g. "Overview") at the edges
            // doesn't overflow off the card.
            const labelAnchor =
              p.pct <= 5
                ? "left-0 translate-x-0"
                : p.pct >= 95
                ? "right-0 translate-x-0"
                : "left-1/2 -translate-x-1/2";
            return (
              <button
                key={p.id}
                data-testid="phase-marker"
                data-phase={p.id}
                data-active={isActive ? "true" : "false"}
                onClick={() => onSelect(p.id)}
                className="absolute -translate-x-1/2 group"
                style={{ left: `${p.pct}%` }}
              >
                <span
                  className={[
                    "block h-3 w-3 rounded-full ring-2 transition",
                    isActive
                      ? "bg-el-500 ring-el-200"
                      : "bg-white ring-slate-300 group-hover:ring-el-300",
                  ].join(" ")}
                />
                <div
                  className={[
                    "absolute mt-2 whitespace-nowrap text-[0.7rem]",
                    labelAnchor,
                    isActive ? "text-el-700 font-semibold" : "text-slate-500",
                  ].join(" ")}
                >
                  {p.label || p.id}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-9 flex items-baseline justify-between border-t border-slate-100 pt-3">
        <div>
          <div className="text-xs text-slate-500">Selected phase</div>
          <div className="text-base font-semibold text-slate-900 tracking-tightish">
            {current?.label ?? active}
          </div>
        </div>
        <div className="text-xs text-slate-500">
          {Math.round(current?.pct ?? 0)}% of {copy.unit}
        </div>
      </div>
    </div>
  );
}
